import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  Building2,
  KeyRound,
  ArrowRight,
} from "lucide-react";
import api from "@/lib/api-client";

interface InvitationData {
  user: {
    email: string;
    firstName: string;
    lastName: string;
  };
  organization: {
    id: string;
    name: string;
  };
}

type InvitationStatus =
  | "loading"
  | "valid"
  | "invalid"
  | "accepting"
  | "success"
  | "error";

export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [status, setStatus] = useState<InvitationStatus>("loading");
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [error, setError] = useState<string>("");

  // Form state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setStatus("invalid");
        setError("Token de invitación no válido");
        return;
      }

      try {
        const response = await api.get(`/invitations/${token}`);
        if (response.data.success) {
          setInvitation(response.data.data);
          setStatus("valid");
        } else {
          setStatus("invalid");
          setError(response.data.message || "Invitación no válida");
        }
      } catch (err: unknown) {
        setStatus("invalid");
        const errorMessage =
          err instanceof Error
            ? err.message
            : (err as { response?: { data?: { message?: string } } })?.response
                ?.data?.message || "Error al validar la invitación";
        setError(errorMessage);
      }
    };

    validateToken();
  }, [token]);

  // Handle accept invitation
  const handleAccept = async () => {
    if (changePassword) {
      if (newPassword.length < 8) {
        setError("La contraseña debe tener al menos 8 caracteres");
        return;
      }
      if (newPassword !== confirmPassword) {
        setError("Las contraseñas no coinciden");
        return;
      }
    }

    setStatus("accepting");
    setError("");

    try {
      const response = await api.post(`/invitations/${token}/accept`, {
        newPassword: changePassword ? newPassword : undefined,
      });

      if (response.data.success) {
        setStatus("success");
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setStatus("error");
        setError(response.data.message || "Error al aceptar la invitación");
      }
    } catch (err: unknown) {
      setStatus("error");
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || "Error al aceptar la invitación";
      setError(errorMessage);
    }
  };

  // Loading state
  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-violet-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verificando invitación...</p>
        </div>
      </div>
    );
  }

  // Invalid token
  if (status === "invalid") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Invitación no válida
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/login")}
            className="bg-gray-900 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
          >
            Ir al inicio de sesión
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            ¡Bienvenido/a!
          </h1>
          <p className="text-gray-600 mb-2">
            Tu cuenta ha sido activada correctamente.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Serás redirigido al inicio de sesión en unos segundos...
          </p>
          <Loader2 className="w-6 h-6 text-green-600 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Valid invitation - show acceptance form
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/logo-horizontal.png"
            alt="ReservaManager"
            className="h-10 w-auto mx-auto mb-2"
          />
          <p className="text-gray-600">Invitación al equipo</p>
        </div>

        {/* User info */}
        <div className="bg-violet-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-gray-600 mb-1">
            Te han invitado a unirte a:
          </p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {invitation?.organization.name}
              </p>
              <p className="text-sm text-gray-600">
                {invitation?.user.firstName} {invitation?.user.lastName}
              </p>
            </div>
          </div>
        </div>

        {/* Email display */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tu email
          </label>
          <div className="bg-gray-100 px-4 py-3 rounded-xl text-gray-700">
            {invitation?.user.email}
          </div>
        </div>

        {/* Password change option */}
        <div className="mb-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={changePassword}
              onChange={(e) => setChangePassword(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
            />
            <span className="text-gray-700">
              <KeyRound className="w-4 h-4 inline mr-1" />
              Quiero cambiar mi contraseña
            </span>
          </label>
        </div>

        {/* Password fields (if changing) */}
        {changePassword && (
          <div className="space-y-4 mb-6 animate-in slide-in-from-top-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nueva contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmar contraseña
              </label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contraseña"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              />
            </div>
          </div>
        )}

        {/* Error message */}
        {error && status === "error" && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        {/* Submit button */}
        <button
          onClick={handleAccept}
          disabled={status === "accepting"}
          className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white py-4 rounded-xl font-semibold hover:from-violet-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-200"
        >
          {status === "accepting" ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Activando cuenta...
            </>
          ) : (
            <>
              Aceptar invitación
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        {/* Security note */}
        <p className="text-xs text-gray-500 text-center mt-6">
          🔒 Al aceptar, tu cuenta será activada y podrás iniciar sesión
          {changePassword
            ? " con tu nueva contraseña"
            : " con la contraseña que te proporcionaron"}
          .
        </p>
      </div>
    </div>
  );
}
